import { Navigate } from 'react-router-dom';
import { useBrand } from '@/contexts/BrandContext';
import { Loader2 } from 'lucide-react';

/**
 * RootLandingRedirect - Intelligent redirect component for root route
 *
 * Routes users to the appropriate landing page based on brand context:
 * - Platform mode → Platform landing page (multi-brand showcase)
 * - Custom domain brand → Brand-specific landing
 *
 * MindCo Health is now accessible via /mindco-health path, not on root.
 *
 * @example
 * ```tsx
 * // In App.tsx
 * <Route path="/" element={<RootLandingRedirect />} />
 * ```
 */
export const RootLandingRedirect: React.FC = () => {
  const { isPlatformMode, brand, isLoading } = useBrand();

  // Debug log to verify detection
  console.log('[RootLandingRedirect] isPlatformMode:', isPlatformMode, 'brand:', brand?.slug, 'isLoading:', isLoading);

  // Show loader while determining brand
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Platform mode → Redirect to platform landing route
  if (isPlatformMode) {
    console.log('[RootLandingRedirect] Redirecting to /platform');
    return <Navigate to="/platform" replace />;
  }

  // Custom domain brand → Redirect to brand's landing page
  if (brand?.slug) {
    return <Navigate to={`/b/${brand.slug}`} replace />;
  }

  // Fallback → Platform landing
  return <Navigate to="/platform" replace />;
};
